import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faVolumeUp, faVolumeMute, faDownload } from "@fortawesome/free-solid-svg-icons";

const CustomAudioPlayer = ({ src }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    // Formate le temps en MM:SS
    const formatTime = (time) => {
        if (isNaN(time)) return "00:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const togglePlay = () => {
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        const current = audioRef.current.currentTime;
        const total = audioRef.current.duration;
        setCurrentTime(current);
        if (total) {
            setProgress((current / total) * 100);
        }
    };

    const handleLoadedMetadata = () => {
        setDuration(audioRef.current.duration);
    };

    const handleProgressChange = (e) => {
        const newTime = (e.target.value / 100) * duration;
        audioRef.current.currentTime = newTime;
        setProgress(e.target.value);
    };
    
    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
    };

    const toggleMute = () => {
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    return (
        <div className="relative bg-gradient-to-r from-teal-50 to-emerald-50 rounded-[2rem] p-3 border border-teal-100 shadow-sm transition-all duration-300 hover:shadow-md group/audioplayer">
            <audio 
                ref={audioRef} 
                src={src} 
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                className="hidden" // On masque le lecteur natif
            />
            
            <div className="flex items-center gap-4 px-2">
                {/* Bouton Play/Pause avec effet */}
                <button 
                    onClick={togglePlay}
                    className="relative w-12 h-12 flex-shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center text-white shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none"
                    aria-label={isPlaying ? "Pause" : "Lecture"}
                >
                    <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover/audioplayer:opacity-100 transition-opacity"></div>
                    {/* Ring animée pendant la lecture */}
                    {isPlaying && (
                        <div className="absolute -inset-1 border-2 border-teal-400 rounded-full animate-ping opacity-20"></div>
                    )}
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className={`text-lg ${!isPlaying ? 'ml-1' : ''}`} />
                </button>

                <div className="flex-1 flex flex-col justify-center gap-1.5">
                    {/* Visualiseur de fréquences (Design) */}
                    <div className="flex items-end gap-1 h-6 px-1">
                        {[...Array(12)].map((_, i) => (
                            <div 
                                key={i} 
                                className={`w-full max-w-[4px] rounded-t-sm bg-gradient-to-t from-emerald-500 to-teal-400 opacity-80 ${isPlaying ? 'animate-bounce' : 'h-1.5 transition-all duration-500'}`}
                                style={{ 
                                    animationDuration: `${0.5 + Math.random()}s`,
                                    animationDelay: `${Math.random() * 0.5}s`,
                                    height: isPlaying ? '100%' : '6px'
                                }}
                            ></div>
                        ))}
                    </div>

                    {/* Barre de progression avec Range Input caché */}
                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden flex items-center group/range cursor-pointer">
                        {/* Fill interne */}
                        <div 
                            className="absolute left-0 top-0 h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full z-10 pointers-none"
                            style={{ width: `${progress}%` }}
                        ></div>
                        {/* Range Input invisible pour interagir */}
                        <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={progress} 
                            onChange={handleProgressChange}
                            className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
                        />
                    </div>

                    {/* Temps et contrôles subsidiaires */}
                    <div className="flex justify-between items-center text-xs font-bold text-gray-500 mt-0.5">
                        <span className="font-mono bg-white/70 px-1.5 rounded text-teal-700 tabular-nums">
                            {formatTime(currentTime)}
                        </span>
                        
                        <div className="flex items-center gap-3">
                            <button onClick={toggleMute} className="text-gray-400 hover:text-teal-600 transition-colors px-1" title="Muet/Son">
                                 <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
                            </button>
                            <a href={src} download target="_blank" rel="noreferrer" className="text-gray-400 hover:text-teal-600 transition-colors px-1 flex items-center gap-1" title="Télécharger le fichier audio">
                                <FontAwesomeIcon icon={faDownload} />
                            </a>
                        </div>
                        
                        <span className="font-mono bg-white/70 px-1.5 rounded text-gray-600 tabular-nums">
                            {formatTime(duration)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomAudioPlayer;
